import { useEffect, useRef, useState } from "react";
import Router, { useRouter } from "next/router";

import { ROUTE_LOADER, RouteLoaderApp } from "../constants/route-loader";

const normalizeRoutePath = (path: string) => path.split("?")[0].split("#")[0];

const stripLocaleFromPath = (path: string) => {
    const withoutLocale = path.replace(ROUTE_LOADER.localePrefixPattern, "");
    return withoutLocale === "" ? "/" : withoutLocale;
};

const isTransitionLoaderRoute = (url: string, app: RouteLoaderApp) => {
    const path = stripLocaleFromPath(normalizeRoutePath(url));
    return ROUTE_LOADER.serverRenderedPaths[app].some((pattern) => pattern.test(path));
};

const isTransitionLoaderNavigation = (nextUrl: string, currentAsPath: string, app: RouteLoaderApp) => {
    if (normalizeRoutePath(nextUrl) === normalizeRoutePath(currentAsPath)) {
        return false;
    }
    return isTransitionLoaderRoute(nextUrl, app);
};

let manualLoaderActive = false;
const manualLoaderListeners = new Set<() => void>();

function notifyManualLoaderListeners() {
    manualLoaderListeners.forEach((listener) => listener());
}

/** Show the global truck loader immediately (e.g. before router.push resolves). */
export function startRouteTransitionLoader() {
    if (manualLoaderActive) {
        return;
    }

    manualLoaderActive = true;
    notifyManualLoaderListeners();
}

export function stopRouteTransitionLoader() {
    if (!manualLoaderActive) {
        return;
    }

    manualLoaderActive = false;
    notifyManualLoaderListeners();
}

const useRouteTransitionLoader = (app: RouteLoaderApp) => {
    const [isRouterLoading, setIsRouterLoading] = useState(false);
    const [, setManualLoaderRevision] = useState(0);
    const router = useRouter();
    const currentPathRef = useRef(router.asPath);

    useEffect(() => {
        const onManualLoaderChange = () => setManualLoaderRevision((revision) => revision + 1);
        manualLoaderListeners.add(onManualLoaderChange);

        return () => {
            manualLoaderListeners.delete(onManualLoaderChange);
        };
    }, []);

    useEffect(() => {
        currentPathRef.current = router.asPath;
    }, [router.asPath]);

    useEffect(() => {
        const handleRouteChangeStart = (url: string) => {
            if (isTransitionLoaderNavigation(url, currentPathRef.current, app)) {
                setIsRouterLoading(true);
            }
        };
        const handleRouteChangeEnd = () => {
            setIsRouterLoading(false);
            stopRouteTransitionLoader();
        };

        Router.events.on("routeChangeStart", handleRouteChangeStart);
        Router.events.on("routeChangeComplete", handleRouteChangeEnd);
        Router.events.on("routeChangeError", handleRouteChangeEnd);

        return () => {
            Router.events.off("routeChangeStart", handleRouteChangeStart);
            Router.events.off("routeChangeComplete", handleRouteChangeEnd);
            Router.events.off("routeChangeError", handleRouteChangeEnd);
        };
    }, [app]);

    return isRouterLoading || manualLoaderActive;
};

export default useRouteTransitionLoader;
